/**
 * The last kind each agent was seen running, kept across reloads.
 *
 * `stickyKind` stopped the view flapping while a page stayed open, but the memory
 * lived in a ref — so loading the page WHILE a host was down started from nothing,
 * resolved to `terminal-view`, and put the owner back in the mode they had not
 * chosen. The gap is narrow and entirely real: the hosts that drop are exactly the
 * ones likely to be down when you next open the app.
 *
 * localStorage rather than sessionStorage: the point is to survive the reload, and a
 * remembered kind is a display hint, not state anything trusts. A live poll always
 * wins over it — see `stickyKind`, which only consults memory when the poll has no
 * answer at all.
 *
 * Every operation swallows its own failure. Storage throws in private mode and when
 * a quota is full, and losing the view preference must never take the app with it.
 */
const KEY = 'wt_agent_kinds'
/** Enough for far more agents than a roster holds, small enough to never matter. */
const MAX = 200

type Kinds = Record<string, string>

export function loadKinds(): Kinds {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const v: unknown = JSON.parse(raw)
    // Shape-checked rather than trusted: this is user-writable storage, and a
    // non-string value reaching `viewForKind` would resolve to the terminal — the
    // exact symptom this exists to prevent, arriving through the fix for it.
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {}
    const out: Kinds = {}
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === 'string' && val) out[k] = val
    }
    return out
  } catch {
    return {}
  }
}

export function saveKind(kinds: Kinds, key: string, kind: string): Kinds {
  if (kinds[key] === kind) return kinds // no write when nothing changed
  const next = { ...kinds, [key]: kind }
  const keys = Object.keys(next)
  if (keys.length > MAX) delete next[keys[0]!]
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // Full or unavailable. The in-memory copy still works for this page.
  }
  return next
}
